import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import TextBox from './TextBox';
import CheckBox from './CheckBox';
import SecondaryButton from './SecondaryButton';
import _ from 'lodash';
import { getTypedFastStorageName, useTypedFastStorage } from '../hooks/useFastStorage';
import fastStorage from '../fastStorage';
import useDeepCompareEffect from '../hooks/useDeepCompareEffect';
import { stringArraysEqual } from '../util';
import { HeaderFilterType } from '../tableSettings';
import Date from '../ui/Date';
import Float from './Float';
import Select, { SelectItem } from './Select';
import InlineInputControlRowNoLabel from './layout/InlineInputControlRowNoLabel';
import { compareIso } from '../dateUtils';
import periodCloseIcon from '../img/PeriodClose.svg';
import periodCloseIcon2 from '../img/periodclose-12.svg';
import opCloseIcon from '../img/OPClose.svg';
import opCloseIcon2 from '../img/opclose-12.svg';

export type FilterCellStorageProps = {
  filterHeaderIndex: number,
  filterCellId: string,
  filterCellTexts?: string[], //TextSelect
  filterCellText?: string, //TextBegin
  filterCellTextType?: string, //TextBegin
  filterFromDate?: string, //Date
  filterToDate?: string, //Date
  filterFromNumber?: string, //Number
  filterToNumber?: string, //Number
}

function getHeaderFilterStorageName(currentUrl: string) {
  return getTypedFastStorageName(`header-filter-column-${currentUrl}`, currentUrl);
}

/**
 * 현재 화면(currentUrl)에 적용된 Header Filter가 있는지 확인
 */
export function hasActiveHeaderFilter(currentUrl: string): boolean {
  const filterItems = fastStorage[getHeaderFilterStorageName(currentUrl)] as FilterCellStorageProps[] | undefined;
  return !!filterItems?.some(item =>
    (item.filterCellTexts?.length ?? 0) > 0 ||
    !!item.filterCellText ||
    !!item.filterFromDate || !!item.filterToDate ||
    !!item.filterFromNumber || !!item.filterToNumber);
}

/**
 * 현재 화면(currentUrl)의 Header Filter를 모두 해제
 */
export function clearHeaderFilters(currentUrl: string) {
  delete fastStorage[getHeaderFilterStorageName(currentUrl)];

  const el = document.createElement('div');
  el.dataset.clearheaderfilterwhenclick = 'true';
  document.body.appendChild(el);
  el.click();
  el.remove();
}

type FilterSelectItemProps = {
  label: string,
  value: string,
}

type FilterListProps = {
  cellId: string,
  cellValues: FilterSelectItemProps[],
}

type TableHeaderFiltersProps = {
  changeListData: (data: Partial<any>) => void;
  filterOpenHeaderIndex: number;
  oriListData: any[];
  filteredHeaderIndex: number[];
  setFilteredHeaderIndex: (index: number[], deleteCurrentHeaderIndex?: boolean) => void;
  modalPosition: { top: number, left: number };
  headerFilterType: HeaderFilterType,
  cellId: string,
  currentUrl: string,
  closeModal: () => void,
  useLowerHeight: boolean
};

type FilterDetailsProps = {
  changeListData: (data: Partial<any>) => void;
  filterOpenHeaderIndex: number;
  oriListData: any[];
  filteredHeaderIndex: number[];
  setFilteredHeaderIndex: (index: number, deleteCurrentHeaderIndex?: boolean) => void;
  headerFilterType: HeaderFilterType;
  cellId: string;
  currentUrl: string;
  useLowerHeight:boolean;
}

type TextSelectRendererProps = {
  changeListData: (data: Partial<any>) => void;
  filterOpenHeaderIndex: number;
  setFilteredHeaderIndex: (index: number, deleteCurrentHeaderIndex?: boolean) => void;
  currentUrl: string;
  getFilteredListData: () => any[];
  cellId: string
  oriListData: any[],
  filteredHeaderIndex: number[],
  useLowerHeight: boolean
}

type NumberRendererProps = {
  changeListData: (data: Partial<any>) => void;
  filterOpenHeaderIndex: number;
  setFilteredHeaderIndex: (index: number, deleteCurrentHeaderIndex?: boolean) => void;
  cellId: string;
  currentUrl: string;
  getFilteredListData: () => any[];
}

type DateRendererProps = {
  changeListData: (data: Partial<any>) => void;
  filterOpenHeaderIndex: number;
  setFilteredHeaderIndex: (index: number, deleteCurrentHeaderIndex?: boolean) => void;
  cellId: string;
  currentUrl: string;
  getFilteredListData: () => any[];
}

type TextBeginRendererProps = {
  changeListData: (data: Partial<any>) => void;
  setFilteredHeaderIndex: (index: number, deleteCurrentHeaderIndex?: boolean) => void;
  filterOpenHeaderIndex: number;
  cellId: string;
  currentUrl: string;
  getFilteredListData: () => any[];
}

type FilterDetailsHandle = {
  clearFilter: () => void;
};

const HeaderFilterStyle = styled.div`
width:250px;

.header-filter-icon {
  padding-left: 1px; 
  padding-right: 2px; 
}

.header-filter-modal {
  position:static;
  border: 1px solid #888;
  background-color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  cursor:default;
  top:30px;
  display: flex;
  flex-direction: column;
}

.header-filter-modal-content {
  padding: 5px;
}

.header-filter-modal-checklist{
position: relative;
  border: 1px solid #808080;
  margin-top: 8px;
  padding: 3px;
  overflow-y: auto;
  width:235px;
  min-height : 100px;
  max-height : 190px;
}

.header-filter-footer {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 45px;
  border-top: 1px solid gray;
}
`;

const customModalStyles: React.CSSProperties = {
  position: 'absolute',
  padding: '0px',
  background: 'transparent',
  borderRadius: 0,
  border: 'none',
  zIndex: 888890,
  width: 250
};

function getFilteredListByNumber(fromValue: number | undefined, toValue: number | undefined, oriListData: any[], cellId: string) {
  oriListData = oriListData.filter((listdata) => {
    const cellValue = listdata[cellId];
    const isEmptyValue = cellValue === undefined || cellValue === null || Number.isNaN(cellValue);

    if (fromValue === undefined && toValue === undefined) {
      return true;
    } else if (fromValue !== undefined && toValue !== undefined) {
      return fromValue <= cellValue && cellValue <= toValue && !isEmptyValue;
    } else if (fromValue !== undefined) {
      return fromValue <= cellValue && !isEmptyValue;
    } else if (toValue !== undefined) {
      return cellValue <= toValue && !isEmptyValue;
    }
    return true;
  });

  return oriListData;
}

function getFilteredListByDate(fromValue: string, toValue: string, oriListData: any[], cellId: string) {
  oriListData = oriListData.filter((listdata) => {
    const cellValue = listdata[cellId];
    const isEmptyValue = _.isEmpty(cellValue);
    if (_.isEmpty(fromValue) && _.isEmpty(toValue)) {
      return true;
    } else if (!_.isEmpty(fromValue) && !_.isEmpty(toValue)) {
      return compareIso(fromValue, cellValue) <= 0 && compareIso(cellValue, toValue) <= 0 && !isEmptyValue;
    } else if (!_.isEmpty(fromValue)) {
      return compareIso(fromValue, cellValue) <= 0 && !isEmptyValue;
    } else if (!_.isEmpty(toValue)) {
      return compareIso(cellValue, toValue) <= 0 && !isEmptyValue;
    }
    return true;
  });

  return oriListData;
}

function getFilteredListByTextBegin(text: string, textType: string, oriListData: any[], cellId: string) {
  const lowerTextValue = text.toLowerCase();
  if (textType === 'contains') {
    oriListData = oriListData?.filter(y => y[cellId].toLowerCase().includes(lowerTextValue));
  }
  else if (textType === 'beginwith') {
    oriListData = oriListData?.filter(y => y[cellId].toLowerCase().startsWith(lowerTextValue));
  }
  return oriListData;
}

const TextSelectRenderer = forwardRef<FilterDetailsHandle, TextSelectRendererProps>(function TextSelectRenderer({
  filterOpenHeaderIndex,
  changeListData,
  setFilteredHeaderIndex,
  currentUrl,
  getFilteredListData,
  cellId,
  oriListData,
  filteredHeaderIndex,
  useLowerHeight
}: TextSelectRendererProps, ref) {
  const [filterItemStorage, setFilterItemStorage] = useTypedFastStorage<FilterCellStorageProps[]>(`header-filter-column-${currentUrl}`, []);
  const [textValue, setTextValue] = useState<string>('');
  const [filterItem, setFilterItem] = useState<FilterListProps>({ cellId: '', cellValues: [] });
  const [displayFilterItem, setDisplayFilterItem] = useState<FilterListProps>({ cellId: '', cellValues: [] });

  useImperativeHandle(ref, () => ({
    clearFilter,
  }));

  useEffect(() => {
    const lowerTextValue = textValue?.toLowerCase();

    const filteredValues = textValue
      ? filterItem.cellValues.filter(x => x?.label.toLowerCase().includes(lowerTextValue))
      : filterItem.cellValues;

    const newValues = (() => {
      if (filteredValues.length === 0) {
        return [];
      }

      if (filteredValues.length === 1 && filteredValues[0].label === "(All)") {
        return [];
      }

      if (textValue) {
        return [{ label: "(All)", value: "(All)" }, ...filteredValues];
      }

      return filteredValues;
    })();

    const uniqueValues: FilterSelectItemProps[] = Array.from(
      new Map(newValues.map(item => [item.value, item])).values()
    );

    setDisplayFilterItem({
      cellId: filterItem.cellId,
      cellValues: uniqueValues,
    });
  }, [textValue, filterItem]);

  useDeepCompareEffect(() => {
    if (!filterItemStorage) return;

    let newFilteredList = getFilteredListData();

    filterItemStorage.forEach(x => {
      if ((x.filterCellTexts?.length ?? 0) > 0)
        newFilteredList = newFilteredList?.filter(y => x.filterCellTexts?.includes(y[x.filterCellId]));
    });

    changeListData(newFilteredList)

    if (filterItemStorage.some(x => x.filterHeaderIndex === filterOpenHeaderIndex && ((x.filterCellTexts?.length ?? 0) > 0))) {
      setFilteredHeaderIndex(filterOpenHeaderIndex);
    }
    else {
      setFilteredHeaderIndex(filterOpenHeaderIndex, true);
    }
    const newCellData = getFilterSelectList();
    const updatedCellValues = [{ label: "(All)", value: "(All)" }, ...newCellData.cellValues];

    setFilterItem({ cellId: newCellData.cellId, cellValues: updatedCellValues });
    setDisplayFilterItem({ cellId: newCellData.cellId, cellValues: updatedCellValues });

  }, [filterItemStorage, filterOpenHeaderIndex]);

  function clearFilter() {
    if (!filterItemStorage) return;

    const newFilterItem = filterItemStorage.map(x => {
      if (x.filterCellId === displayFilterItem.cellId) x.filterCellTexts = [];
      return x;
    });
    setFilterItemStorage(newFilterItem)
  }

  function onFilterItemCheck(check: boolean, addFilterCellId: string, addFilterText: string) {
    const checkAllItem = addFilterText === "(All)";
    const filterItemIndex = filterItemStorage?.findIndex(x => x.filterCellId === addFilterCellId);

    if (check) {
      if (!filterItemStorage || filterItemIndex === -1) {
        const newItem: FilterCellStorageProps = {
          filterHeaderIndex: filterOpenHeaderIndex,
          filterCellId: addFilterCellId,
          filterCellTexts: checkAllItem ? displayFilterItem.cellValues.map(x => x.value) : [addFilterText]
        };

        const updatedStorage = filterItemStorage ? [..._.cloneDeep(filterItemStorage), newItem] : [newItem];
        setFilterItemStorage(updatedStorage);
      } else {
        let addTexts: string[] = [];
        let updatedStorage = _.cloneDeep(filterItemStorage).map((item) => {
          if (item.filterCellId === addFilterCellId) {
            addTexts = checkAllItem ? displayFilterItem.cellValues.map(x => x.value) : [...item.filterCellTexts ?? [], addFilterText];
            if (!checkAllItem && stringArraysEqual(addTexts, displayFilterItem.cellValues.map(x => x.value).filter(x => x !== "(All)"))) addTexts.unshift("(All)");
            return { ...item, filterCellTexts: addTexts };
          }
          return item;
        });

        setFilterItemStorage(updatedStorage);
      }
    }
    else {
      if (filterItemStorage) {
        const updatedStorage = _.cloneDeep(filterItemStorage).map((item, index) => {
          if (index === filterItemIndex) {
            let updatedFilterCellTexts = checkAllItem ? [] : item.filterCellTexts?.filter(text => text !== addFilterText);
            if (!checkAllItem) updatedFilterCellTexts = updatedFilterCellTexts?.filter(x => x !== "(All)");
            return {
              ...item,
              filterCellTexts: updatedFilterCellTexts
            };
          }
          return item;
        })
        setFilterItemStorage(updatedStorage);
      }
    }
  }

  //본인 포함 모든 filter를 적용한 list의 value, cellid => for "TextSelect"
  function getFilterSelectList(): FilterListProps {
    interface RowType {
      [key: string]: any;
    }

    if (filterOpenHeaderIndex <= 0) {
      return { cellValues: [], cellId: '' };
    }

    let typedOriListData: RowType[] = oriListData as RowType[];

    if (filteredHeaderIndex?.length ?? 0 > 1) { //is multilple filter
      filterItemStorage?.forEach(x => {
        if (x.filterCellId !== cellId) {
          if (x.filterCellText && x.filterCellTextType) { //TextBegin Filter 반영
            typedOriListData = getFilteredListByTextBegin(x.filterCellText, x.filterCellTextType, typedOriListData, x.filterCellId);
          }
          else if (x.filterCellTexts && x.filterCellTexts.length > 0) { //TextSelect Filter 반영
            typedOriListData = typedOriListData?.filter(y => x.filterCellTexts?.includes(y[x.filterCellId]));
          }
          else if (x.filterToDate || x.filterFromDate) { //Date Filter 반영
            const findFromValue = x.filterFromDate ?? '';
            const findToValue = x.filterToDate ?? '';
            typedOriListData = getFilteredListByDate(findFromValue, findToValue, typedOriListData, x.filterCellId);
          }
          else if (x.filterFromNumber || x.filterToNumber) { //Number Filter 반영
            const findFromValue = !_.isEmpty(x.filterFromNumber) ? Number(x.filterFromNumber) : undefined
            const findToValue = !_.isEmpty(x.filterToNumber) ? Number(x.filterToNumber) : undefined;
            typedOriListData = getFilteredListByNumber(findFromValue, findToValue, typedOriListData, x.filterCellId);
          }
        }
      });
    }

    const uniqueValues: FilterSelectItemProps[] = Array.from(new Set(
      typedOriListData.map(row => row[cellId])
    ))
      .sort()
      .reduce((acc, value) => {
        let label;
        if (value === "" || (cellId === "closed" && value === 0)) {
          label = "(Blank)";
        } else {
          label = value;
        }

        const item = { label, value };

        if (value === "" || (cellId === "closed" && value === 0)) {
          acc.unshift(item);
        } else {
          acc.push(item);
        }
        return acc;
      }, [] as FilterSelectItemProps[]);
    
    console.log(uniqueValues);

    return { cellValues: uniqueValues, cellId: cellId };
  }

  function getCellLabel(cellLabel: any) {
    if (cellLabel === 1 && cellId === 'closed') { //Sales / Cose Batch Recog > "+" 
      return <span><img height="12px" src={opCloseIcon2} alt="opClose" /> (OP Close)</span>;
    } else if (cellLabel === 2 && cellId === 'closed') { //Sales / Cose Batch Recog > "+" 
      return <span><img height="12px" src={periodCloseIcon2} alt="periodClose" /> (Period Close)</span>;
    }

    return cellLabel;
  }

  return (
    <>
      <div style={{ width: '235px' }}>
        <TextBox
          value={textValue}
          placeholder="Enter text to search..."
          onChange={(e) => {
            setTextValue(e.target.value);
          }}
        />
      </div>
      <div className='header-filter-modal-checklist' style={{height : useLowerHeight ? '100px' : '170px'}}>
        {displayFilterItem.cellValues.map((cellValue, i) => (
          <div
            key={i}
            style={{ paddingBottom: '6px', alignItems: 'start', justifyContent: 'start', height: '18px' }}
            data-tooltip-onlywhencutoff
          >
            <CheckBox
              label={getCellLabel(cellValue.label)}
              value={filterItemStorage?.filter(x => x.filterCellId === displayFilterItem.cellId)?.some(x => x.filterCellTexts?.includes(cellValue.value)) ?? false}
              onChange={check => {
                onFilterItemCheck(check, displayFilterItem.cellId, cellValue.value);
              }}
            />
          </div>
        ))}
      </div>
    </>
  )
});

const TextBeginRenderer = forwardRef<FilterDetailsHandle, TextBeginRendererProps>(function TextBeginRenderer({
  changeListData,
  cellId,
  filterOpenHeaderIndex,
  setFilteredHeaderIndex,
  currentUrl,
  getFilteredListData,
}: TextBeginRendererProps, ref) {
  const defaultFilterType = 'beginwith';
  const [filterItemStorage, _setFilterItemStorage] = useTypedFastStorage<FilterCellStorageProps[]>(`header-filter-column-${currentUrl}`, []);
  const [textValue, setTextValue] = useState<string>('');
  const [filterTypeValue, setFilterTypeValue] = useState<string>(defaultFilterType);
  const filterType: SelectItem[] = [
    { value: 'beginwith', label: 'Begins With' },
    { value: 'contains', label: 'Contains' }
  ];

  useImperativeHandle(ref, () => ({
    clearFilter,
  }));

  useEffect(() => {
    const filterCellText = filterItemStorage?.find(x => x.filterCellId === cellId)?.filterCellText;
    setTextValue(filterCellText && !_.isEmpty(filterCellText) ? filterCellText : '');
    const filterCellTextType = filterItemStorage?.find(x => x.filterCellId === cellId)?.filterCellTextType;
    setFilterTypeValue(filterCellTextType && !_.isEmpty(filterCellTextType) ? filterCellTextType : defaultFilterType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useDeepCompareEffect(() => {
    if (!filterItemStorage) return;

    let newFilteredList = getFilteredListData();
    const findTextValue = filterItemStorage.find(x => x.filterCellId === cellId)?.filterCellText ?? '';
    const findFilterTypeValue = filterItemStorage.find(x => x.filterCellId === cellId)?.filterCellTextType ?? '';
    if (!_.isEmpty(findTextValue)) {
      newFilteredList = getFilteredListByTextBegin(findTextValue, findFilterTypeValue, newFilteredList, cellId);
    }
    changeListData(newFilteredList);
  }, [filterItemStorage]);

  function setFilterItemStorage(newText: string, newTypeValue: string) {
    const filterItemIndex = filterItemStorage?.findIndex(x => x.filterCellId === cellId);

    if (!filterItemStorage || filterItemIndex === -1) {
      const newItem: FilterCellStorageProps = {
        filterHeaderIndex: filterOpenHeaderIndex,
        filterCellId: cellId,
        filterCellText: newText,
        filterCellTextType: newTypeValue,
      };

      const updatedStorage = filterItemStorage ? [..._.cloneDeep(filterItemStorage), newItem] : [newItem];
      _setFilterItemStorage(updatedStorage);
    } else {
      let updatedStorage = _.cloneDeep(filterItemStorage).map((item) => {
        if (item.filterCellId === cellId) {
          return { ...item, filterCellText: newText, filterCellTextType: newTypeValue };
        }
        return item;
      });

      _setFilterItemStorage(updatedStorage);
    }
    setFilteredHeaderIndex(filterOpenHeaderIndex, _.isEmpty(newText));
  }

  function clearFilter() {
    setTextValue('');
    setFilterTypeValue(String(filterType[0].value));
    setFilterItemStorage('', String(filterType[0].value));
  }

  return (
    <div style={{ width: '235px', marginTop: '4px' }}>
      <InlineInputControlRowNoLabel>
        <Select
          items={filterType}
          value={filterTypeValue}
          onChange={(e) => {
            const targetValue = e.target.value;
            setFilterItemStorage(textValue, targetValue);
            setFilterTypeValue(e.target.value);
          }}
        />
      </InlineInputControlRowNoLabel>
      <InlineInputControlRowNoLabel>
        <TextBox
          value={textValue}
          placeholder="Enter text to search..."
          onChange={(e) => {
            const targetValue = e.target.value;
            setFilterItemStorage(targetValue, filterTypeValue);
            setTextValue(targetValue);
          }}
        />
      </InlineInputControlRowNoLabel>
    </div>
  )
});

const DateRenderer = forwardRef<FilterDetailsHandle, DateRendererProps>(function DateRenderer({
  filterOpenHeaderIndex,
  changeListData,
  setFilteredHeaderIndex,
  cellId,
  currentUrl,
  getFilteredListData,
}: DateRendererProps, ref) {
  const [filterItemStorage, _setFilterItemStorage] = useTypedFastStorage<FilterCellStorageProps[]>(`header-filter-column-${currentUrl}`, []);
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');

  useImperativeHandle(ref, () => ({
    clearFilter,
  }));

  useEffect(() => {
    const filterFromText = filterItemStorage?.find(x => x.filterCellId === cellId)?.filterFromDate;
    setFromValue(filterFromText && !_.isEmpty(filterFromText) ? filterFromText : '');
    const filterToText = filterItemStorage?.find(x => x.filterCellId === cellId)?.filterToDate;
    setToValue(filterToText && !_.isEmpty(filterToText) ? filterToText : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useDeepCompareEffect(() => {
    if (!filterItemStorage) return;

    let newFilteredList = getFilteredListData();
    const findFromValue = filterItemStorage.find(x => x.filterCellId === cellId)?.filterFromDate ?? '';
    const findToValue = filterItemStorage.find(x => x.filterCellId === cellId)?.filterToDate ?? '';
    newFilteredList = getFilteredListByDate(findFromValue, findToValue, newFilteredList, cellId);

    changeListData(newFilteredList);
  }, [filterItemStorage]);


  function setFilterItemStorage(fromValue: string, toValue: string) {
    const filterItemIndex = filterItemStorage?.findIndex(x => x.filterCellId === cellId);

    if (!filterItemStorage || filterItemIndex === -1) {
      const newItem: FilterCellStorageProps = {
        filterHeaderIndex: filterOpenHeaderIndex,
        filterCellId: cellId,
        filterFromDate: fromValue,
        filterToDate: toValue,
      };

      const updatedStorage = filterItemStorage ? [..._.cloneDeep(filterItemStorage), newItem] : [newItem];
      _setFilterItemStorage(updatedStorage);
    } else {
      let updatedStorage = _.cloneDeep(filterItemStorage).map((item) => {
        if (item.filterCellId === cellId) {
          return { ...item, filterFromDate: fromValue, filterToDate: toValue };
        }
        return item;
      });

      _setFilterItemStorage(updatedStorage);
    }

    setFilteredHeaderIndex(filterOpenHeaderIndex, _.isEmpty(fromValue) && _.isEmpty(toValue));
  }

  function clearFilter() {
    setFromValue('');
    setToValue('');
    setFilterItemStorage('', '');
  }

  return (
    <>
      <div style={{ display: 'flex', height: '100%',}}>
        <div style={{ width: '85px' }}>
          <Date
            noRangeCheck
            value={fromValue}
            onChange={(fromDate) => {
              const newFromValue = fromDate ?? '';
              setFromValue(newFromValue);
              setFilterItemStorage(newFromValue, toValue);
            }}
          />
        </div>
        <div style={{ width: '50px', textAlign: 'center', paddingLeft: '10px' }}>
          ~
        </div>
        <div style={{ width: '85px', marginRight: '8px' }}>
          <Date
            noRangeCheck
            value={toValue}
            onChange={(toDate) => {
              const newToValue = toDate ?? '';
              setFilterItemStorage(fromValue, newToValue);
              setToValue(newToValue);
            }}
          />
        </div>
      </div>
    </>
  )
});

const NumberRenderer = forwardRef<FilterDetailsHandle, NumberRendererProps>(function NumberRenderer({
  filterOpenHeaderIndex,
  changeListData,
  setFilteredHeaderIndex,
  cellId,
  currentUrl,
  getFilteredListData,
}: NumberRendererProps, ref) {
  const [filterItemStorage, _setFilterItemStorage] = useTypedFastStorage<FilterCellStorageProps[]>(`header-filter-column-${currentUrl}`, []);
  const [fromValue, setFromValue] = useState<number>();
  const [toValue, setToValue] = useState<number>();

  useImperativeHandle(ref, () => ({
    clearFilter,
  }));

  useEffect(() => {
    const filterFromText = filterItemStorage?.find(x => x.filterCellId === cellId)?.filterFromNumber;
    setFromValue(!_.isEmpty(filterFromText) ? Number(filterFromText) : undefined);
    const filterToText = filterItemStorage?.find(x => x.filterCellId === cellId)?.filterToNumber;
    setToValue(!_.isEmpty(filterToText) ? Number(filterToText) : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useDeepCompareEffect(() => {
    if (!filterItemStorage) return;

    let newFilteredList = getFilteredListData();
    const findFromValue = filterItemStorage.find(x => x.filterCellId === cellId)?.filterFromNumber;
    const findToValue = filterItemStorage.find(x => x.filterCellId === cellId)?.filterToNumber;
    newFilteredList = getFilteredListByNumber(!_.isEmpty(findFromValue) ? Number(findFromValue) : undefined, !_.isEmpty(findToValue) ? Number(findToValue) : undefined, newFilteredList, cellId);

    changeListData(newFilteredList);
  }, [filterItemStorage]);


  function setFilterItemStorage(fromValue: number | undefined, toValue: number | undefined) {
    const filterItemIndex = filterItemStorage?.findIndex(x => x.filterCellId === cellId);

    if (!filterItemStorage || filterItemIndex === -1) {
      const newItem: FilterCellStorageProps = {
        filterHeaderIndex: filterOpenHeaderIndex,
        filterCellId: cellId,
        filterFromNumber: fromValue === undefined ? '' : String(fromValue),
        filterToNumber: toValue === undefined ? '' : String(toValue),
      };

      const updatedStorage = filterItemStorage ? [..._.cloneDeep(filterItemStorage), newItem] : [newItem];
      _setFilterItemStorage(updatedStorage);
    } else {
      let updatedStorage = _.cloneDeep(filterItemStorage).map((item) => {
        if (item.filterCellId === cellId) {
          return { ...item, filterFromNumber: fromValue === undefined ? '' : String(fromValue), filterToNumber: toValue === undefined ? '' : String(toValue) };
        }
        return item;
      });

      _setFilterItemStorage(updatedStorage);
    }

    setFilteredHeaderIndex(filterOpenHeaderIndex, fromValue === undefined && toValue === undefined);
  }

  function clearFilter() {
    setFromValue(undefined);
    setToValue(undefined);
    setFilterItemStorage(undefined, undefined);
  }

  return (
    <>
      <div style={{ width: '235px', flexGrow: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
        <Float
          value={fromValue}
          onChange={(fromNum) => {
            const newFromValue = fromNum ?? undefined;
            setFilterItemStorage(newFromValue, toValue);
            setFromValue(newFromValue);
          }}
        />
        <span style={{ marginLeft: '5px', marginRight: '5px' }}>~</span>
        <Float
          value={toValue}
          onChange={(toNum) => {
            const newToValue = toNum ?? undefined;
            setFilterItemStorage(fromValue, newToValue);
            setToValue(newToValue);
          }}
        />
      </div>
    </>
  )
});

const FilterDetails = forwardRef<FilterDetailsHandle, FilterDetailsProps>(function FilterDetails({
  changeListData,
  oriListData,
  filteredHeaderIndex,
  setFilteredHeaderIndex,
  filterOpenHeaderIndex,
  headerFilterType,
  cellId,
  currentUrl,
  useLowerHeight,
}: FilterDetailsProps, ref) {
  const [filterItemStorage] = useTypedFastStorage<FilterCellStorageProps[]>(`header-filter-column-${currentUrl}`, []);
  const childRef = useRef<FilterDetailsHandle>(null);

  const clearFilter = () => {
    if (childRef.current) {
      childRef.current.clearFilter();
    }
  };

  useImperativeHandle(ref, () => ({
    clearFilter,
  }));

  //본인을 제외한 나머지 filter가 적용된 list
  function getFilteredListData() {
    let targetList = _.cloneDeep(oriListData);

    if (filteredHeaderIndex.length === 0)
      return targetList;
    else {
      filterItemStorage?.forEach((x) => {
        if (x.filterCellId !== cellId) {
          if (x.filterCellTexts && x.filterCellTexts.length > 0) { //TextSelect Filter 반영
            targetList = targetList?.filter(y => x.filterCellTexts?.includes(y[x.filterCellId]));
          }
          else if (x.filterCellText && x.filterCellTextType) { //TextBegin Filter 반영
            targetList = getFilteredListByTextBegin(x.filterCellText, x.filterCellTextType, targetList, x.filterCellId);
          }
          else if (x.filterToDate || x.filterFromDate) { //Date Filter 반영
            const findFromValue = x.filterFromDate ?? '';
            const findToValue = x.filterToDate ?? '';
            targetList = getFilteredListByDate(findFromValue, findToValue, targetList, x.filterCellId);
          }
          else if (x.filterFromNumber || x.filterToNumber) { //Number Filter 반영
            const findFromValue = !_.isEmpty(x.filterFromNumber) ? Number(x.filterFromNumber) : undefined
            const findToValue = !_.isEmpty(x.filterToNumber) ? Number(x.filterToNumber) : undefined;
            targetList = getFilteredListByNumber(findFromValue, findToValue, targetList, x.filterCellId);
          }
        }
      })
    }

    return targetList;
  }

  return (
    <>
      {
        headerFilterType === 'TextBegin' &&
        <TextBeginRenderer
          ref={childRef}
          cellId={cellId}
          changeListData={changeListData}
          setFilteredHeaderIndex={setFilteredHeaderIndex}
          filterOpenHeaderIndex={filterOpenHeaderIndex}
          currentUrl={currentUrl}
          getFilteredListData={getFilteredListData}
        />
      }
      {
        headerFilterType === 'TextSelect' &&
        <TextSelectRenderer
          ref={childRef}
          filterOpenHeaderIndex={filterOpenHeaderIndex}
          changeListData={changeListData}
          setFilteredHeaderIndex={setFilteredHeaderIndex}
          currentUrl={currentUrl}
          getFilteredListData={getFilteredListData}
          cellId={cellId}
          oriListData={oriListData}
          filteredHeaderIndex={filteredHeaderIndex}
          useLowerHeight={useLowerHeight}
        />
      }
      {
        (headerFilterType === 'Date') &&

        <DateRenderer
          currentUrl={currentUrl}
          ref={childRef}
          cellId={cellId}
          filterOpenHeaderIndex={filterOpenHeaderIndex}
          changeListData={changeListData}
          setFilteredHeaderIndex={setFilteredHeaderIndex}
          getFilteredListData={getFilteredListData}
        />
      }
      {
        (headerFilterType === 'Number') &&
        <NumberRenderer
          currentUrl={currentUrl}
          ref={childRef}
          cellId={cellId}
          filterOpenHeaderIndex={filterOpenHeaderIndex}
          changeListData={changeListData}
          setFilteredHeaderIndex={setFilteredHeaderIndex}
          getFilteredListData={getFilteredListData}
        />
      }
    </>
  )
});

function TableHeaderFilters({
  oriListData,
  changeListData,
  filterOpenHeaderIndex,
  setFilteredHeaderIndex,
  modalPosition,
  headerFilterType,
  cellId,
  filteredHeaderIndex,
  currentUrl,
  closeModal,
  useLowerHeight
}: TableHeaderFiltersProps) {
  const FilterDetailsRef = useRef<FilterDetailsHandle>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClearFilter = () => {
    if (FilterDetailsRef.current)
      FilterDetailsRef.current.clearFilter();
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const filterIconContainer = document.getElementById('header-filter-icon-container');
      const target = event.target as Node;

      if (filterIconContainer && filterIconContainer.contains(target)) {
        return;
      }

      if (modalRef.current) {
        const rect = modalRef.current.getBoundingClientRect();
        const { clientX: x, clientY: y } = event;
        const calendar =  document.getElementsByClassName("ifms-date-box")[0] as HTMLDivElement;
        if (!calendar &&  
         ( x < rect.left ||
          x > rect.right ||
          y < rect.top ||
          y > rect.bottom
          )) {
          closeModal();
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyFilteredHeaderIndex(index: number, deleteCurrentHeaderIndex: boolean = false) {
    if (!deleteCurrentHeaderIndex) {
      const updatedSet = new Set([...filteredHeaderIndex, index]);
      setFilteredHeaderIndex(Array.from(updatedSet));
    }
    else
      setFilteredHeaderIndex(filteredHeaderIndex.filter(x => x !== filterOpenHeaderIndex));
  }

  let tempModalLeftPosition = modalPosition.left;
  if(modalRef.current){
    if (modalRef.current.offsetWidth + modalPosition.left > document.body.offsetWidth) {
      tempModalLeftPosition = document.body.offsetWidth - modalRef.current.offsetWidth - 15
    }
  }

  return (
    <Modal
      isOpen={filterOpenHeaderIndex > 0}
      style={{
        overlay: { background: 'transparent', pointerEvents: 'none' },
        content: {
          ...customModalStyles,
          top: modalPosition.top,
          left: tempModalLeftPosition,
          pointerEvents: 'auto',
        },
      }}
    >
      <HeaderFilterStyle>
        <div
          id={`header-filter-modal-${filterOpenHeaderIndex}`}
          className="header-filter-modal"
          ref={modalRef}
        >
          <div className="header-filter-modal-content">
            <FilterDetails
              filteredHeaderIndex={filteredHeaderIndex}
              ref={FilterDetailsRef}
              oriListData={oriListData}
              changeListData={changeListData}
              setFilteredHeaderIndex={applyFilteredHeaderIndex}
              filterOpenHeaderIndex={filterOpenHeaderIndex}
              headerFilterType={headerFilterType}
              cellId={cellId}
              currentUrl={currentUrl}
              useLowerHeight={useLowerHeight}
            ></FilterDetails>
          </div>

          <div className='header-filter-footer'>
            <SecondaryButton
              disabled={!filteredHeaderIndex.includes(filterOpenHeaderIndex)}
              size="regular"
              style={{ width: '80px' }}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleClearFilter();
              }}>
              Clear Filter
            </SecondaryButton>
            <SecondaryButton
              size="regular"
              style={{ width: '60px' }}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                closeModal();
              }}>
              Ok
            </SecondaryButton>
          </div>
        </div>
      </HeaderFilterStyle>
    </Modal>
  );
}

export default React.memo(TableHeaderFilters);
